import React, { useEffect, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { useAppStore } from './hooks/useAppStore';
import { useAssessmentSession } from './hooks/useAssessmentSession';
import { MODULES, getAllQuestions } from './data/questionLoader';
import { AppShellRedesigned } from './components/layout/AppShellRedesigned';
import { ModulePanelRedesigned } from './components/module/ModulePanelRedesigned';
import { PracticeSession } from './components/practice/PracticeSession';
// import { PracticeErrorBoundary } from './components/practice/PracticeErrorBoundary'
import { LevelUpModal } from './components/LevelUpModal';
import { CheatSheetDrawer } from './components/practice/CheatSheetDrawer';
import { SettingsPage } from './components/settings/SettingsPage';
import { ParentPortalPage } from './components/parent/ParentPortalPage';
import { TeacherPortalPage } from './components/teacher/TeacherPortalPage';
import { WelcomeScreen } from './components/auth/WelcomeScreen';
import { LearningLabPage } from './components/learningLab/LearningLabPage';
import { GovernancePage } from './components/governance/GovernancePage';
import { DeveloperPage } from './components/developer/DeveloperPage';
import { HomePage } from './components/home/HomePage';
import { ProgressPageRedesigned } from './components/progress/ProgressPageRedesigned';
import { SubjectsPageRedesigned } from './components/subjects/SubjectsPageRedesigned';
import { AdminOverviewPage } from './components/admin/AdminOverviewPage';
import { ModuleId, Question, ModuleData } from './types';
import { createModeSession } from './modes/modeEngine';
import { getWeakestFamilies } from './modes/modeSelectors';
import { FluencyConfig, SprintConfig, ChallengeConfig, ModeId as ModeIdType } from './modes/types';
import { AssessmentStartPage } from './components/assessment/AssessmentStartPage';
import { AssessmentReportPage } from './components/assessment/AssessmentReportPage';
import { getAssessmentHistory } from './components/assessment/utils/assessmentStorage';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
// import { GlobalErrorBoundary } from './components/shared/GlobalErrorBoundary'
import { AssessmentRecoveryModal } from './components/assessment/AssessmentRecoveryModal';
import { ValidationErrorModal } from './components/shared/ValidationErrorModal';
import { AssessmentErrorModal } from './components/shared/AssessmentErrorModal';
import { ViewingLearnerBanner } from './components/shared/ViewingLearnerBanner';
import { assertSupportCoverage } from './support/supportCoverageReport';
import { validateSessionAvailability, logValidationReport } from './utils/sessionValidation';
import { debug, warn, error as logError } from './utils/logger';
import { RotateCcw } from 'lucide-react';
// Safe question loading with error handling
let QUESTIONS: Question[] = [];
try {
  QUESTIONS = getAllQuestions();
  if (QUESTIONS.length === 0) {
    warn('No questions loaded - check question data files');
  }
  if (process.env.NODE_ENV === 'development') {
    assertSupportCoverage(QUESTIONS);
  }
} catch (error) {
  logError('Failed to load questions:', error);
  QUESTIONS = [];
}
type AppView = 'dashboard' | 'practice';
type NavView = 'dashboard' | 'modules' | 'progress' | 'settings' | 'assessment' | 'assessmentReport' | 'parent' | 'teacher' | 'learningLab' | 'governance' | 'developer' | 'admin';
export function App() {
  const {
    state,
    accounts,
    activeAccount,
    selectedGroup,
    setSelectedGroup,
    startingPeriod,
    manualPeriod,
    setStartingPeriod,
    recordAnswer,
    markCheatSheetViewed,
    clearPendingLevelUp,
    updateProfileDisplayName,
    switchAccount,
    isInitialized,
    loadDemoAccounts,
    currentLearnerDisplayName,
    requiresViewingLearnerSelection,
    getLearnerData,
    getLearnerModuleMastery,
    viewingLearnerId,
    setViewingLearner
  } = useAppStore();
  const [currentView, setCurrentView] = useState<AppView>('dashboard');
  const [currentNavView, setCurrentNavView] = useState<NavView>('dashboard');
  const [activeModuleId, setActiveModuleId] = useState<ModuleId | null>(null);
  const [isFocusSession, setIsFocusSession] = useState(false);
  const [focusQuestions, setFocusQuestions] = useState<Question[]>([]);
  const [selectedModule, setSelectedModule] = useState<ModuleData | null>(null);
  const [cheatSheetModuleId, setCheatSheetModuleId] = useState<ModuleId | null>(null);
  const [validationError, setValidationError] = useState<{
    moduleId: ModuleId;
    message: string;
    hasContent: boolean;
  } | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [previousNavView, setPreviousNavView] = useState<NavView | null>(null);
  // Assessment session (extracted hook)
  const assessment = useAssessmentSession({
    familyMastery: state.familyMastery || {},
    selectedGroup,
    recordAnswer,
    allQuestions: QUESTIONS,
    isReady: isInitialized && !showWelcome
  });
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (cheatSheetModuleId) setCheatSheetModuleId(null);else if (selectedModule) setSelectedModule(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cheatSheetModuleId, selectedModule]);
  // Account switching handler
  const handleSwitchAccount = (accountId: string) => {
    switchAccount(accountId);
    setPreviousNavView(null);
    const newAccount = accounts.accountsById[accountId];
    if (newAccount) {
      switch (newAccount.role) {
        case 'learner':
          setCurrentNavView('dashboard');
          break;
        case 'parent':
          setCurrentNavView('parent');
          break;
        case 'teacher':
          setCurrentNavView('teacher');
          break;
        case 'admin':
          setCurrentNavView('admin');
          break;
      }
    }
  };
  const handleLoadDemoAccounts = () => {
    loadDemoAccounts();
    setShowWelcome(false);
  };
  // Check if we should show welcome screen
  useEffect(() => {
    if (isInitialized) {
      const accountsList = Object.values(accounts.accountsById);
      if (accountsList.length === 0) {
        setShowWelcome(true);
      }
    }
  }, [isInitialized, accounts]);
  // ── Cross-Portal Navigation ────────────────────────────────────────
  const handleNavigateToLearner = (learnerId: string) => {
    setViewingLearner(learnerId);
    setPreviousNavView(currentNavView);
    setCurrentNavView('dashboard');
  };
  const handleReturnToPortal = () => {
    setViewingLearner(null);
    if (previousNavView) {
      setCurrentNavView(previousNavView);
      setPreviousNavView(null);
    } else {
      // Fallback based on role
      if (activeAccount?.role === 'parent') setCurrentNavView('parent');else if (activeAccount?.role === 'teacher') setCurrentNavView('teacher');else if (activeAccount?.role === 'admin') setCurrentNavView('admin');else setCurrentNavView('dashboard');
    }
  };
  // ── Practice handlers ──────────────────────────────────────────────
  const handleStartPractice = (moduleId: ModuleId) => {
    const validationReport = validateSessionAvailability(moduleId, selectedGroup);
    if (!validationReport.isValid) {
      if (process.env.NODE_ENV === 'development') {
        warn(`Cannot start practice for ${moduleId} in group ${selectedGroup}:`);
        logValidationReport(validationReport);
      }
      setValidationError({
        moduleId,
        message: validationReport.errors[0] || 'Geen vragen beschikbaar',
        hasContent: validationReport.stats.totalFamilies > 0
      });
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      debug(`Pre-flight check passed for ${moduleId} in group ${selectedGroup}`);
      debug(`   ${validationReport.stats.totalQuestionsAvailable} questions available from ${validationReport.stats.familiesWithQuestions} families`);
    }
    markCheatSheetViewed(moduleId);
    setActiveModuleId(moduleId);
    setCurrentView('practice');
    setIsFocusSession(false);
    setSelectedModule(null);
  };
  const handlePracticeComplete = () => {
    setActiveModuleId(null);
    setCurrentView('dashboard');
    setIsFocusSession(false);
    setFocusQuestions([]);
  };
  // ── Assessment handlers (delegate to hook) ─────────────────────────
  const handleStartAssessment = (config: import('./components/assessment/types').AssessmentConfig) => {
    const result = assessment.startAssessment(config);
    if (result) {
      setActiveModuleId(null);
      setIsFocusSession(true);
      setFocusQuestions(result.questions);
      setCurrentView('practice');
    }
  };
  const handleAssessmentComplete = () => {
    assessment.completeAssessment();
    setCurrentView('dashboard');
    setCurrentNavView('assessmentReport');
    setIsFocusSession(false);
    setFocusQuestions([]);
  };
  const handleResumeAssessment = () => {
    const result = assessment.resumeAssessment();
    if (result) {
      setActiveModuleId(null);
      setIsFocusSession(true);
      setFocusQuestions(result.questions);
      setCurrentView('practice');
    }
  };
  // ── Mode handlers ──────────────────────────────────────────────────
  const handleStartMode = (modeId: string) => {
    try {
      let config: FluencyConfig | SprintConfig | ChallengeConfig;
      switch (modeId) {
        case 'fluency':
          config = {
            duration: 300
          } as FluencyConfig;
          break;
        case 'sprint':
          {
            const weakFamilies = getWeakestFamilies(state, 1);
            if (weakFamilies.length === 0) {
              setValidationError({
                moduleId: 'sprint' as ModuleId,
                message: 'Je hebt nog geen onderwerpen geoefend. Begin eerst met een vak!',
                hasContent: false
              });
              return;
            }
            config = {
              targetFamilyId: weakFamilies[0].familyId,
              targetCount: 10,
              adaptive: true
            } as SprintConfig;
            break;
          }
        case 'challenge':
          {
            const familyMastery = state.familyMastery || {};
            const strongFamilies = Object.entries(familyMastery).filter(([_, m]) => m.masteryScore >= 60).sort((a, b) => b[1].masteryScore - a[1].masteryScore);
            if (strongFamilies.length === 0) {
              setValidationError({
                moduleId: 'challenge' as ModuleId,
                message: 'Je hebt nog geen onderwerpen goed genoeg beheerst (60%+). Oefen eerst meer!',
                hasContent: false
              });
              return;
            }
            config = {
              targetFamilyId: strongFamilies[0][0],
              difficulty: 2
            } as ChallengeConfig;
            break;
          }
        default:
          return;
      }
      const session = createModeSession(modeId as ModeIdType, config, state, selectedGroup);
      setActiveModuleId(null);
      setIsFocusSession(true);
      setFocusQuestions(session.questions);
      setCurrentView('practice');
    } catch (err) {
      logError('Failed to start mode session:', err);
      setValidationError({
        moduleId: modeId as ModuleId,
        message: 'Geen vragen beschikbaar voor deze trainingsmodus. Probeer een ander vak te oefenen.',
        hasContent: false
      });
    }
  };
  // ── Render ─────────────────────────────────────────────────────────
  if (showWelcome) {
    return <MotionConfig reducedMotion="user" data-id="element-1">
        <WelcomeScreen onLoadDemo={handleLoadDemoAccounts} data-id="element-2" />
      </MotionConfig>;
  }
  if (QUESTIONS.length === 0) {
    return <MotionConfig reducedMotion="user" data-id="element-3">
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-id="element-4">
          <Card className="max-w-md w-full p-8 text-center" data-id="element-5">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4" data-id="element-6">
              <AlertTriangle className="w-8 h-8 text-amber-600" data-id="element-7" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-8">
              Vragen laden mislukt
            </h2>
            <Button onClick={() => window.location.reload()} variant="primary" size="lg" data-id="element-9">
              <RotateCcw className="w-4 h-4 mr-2" data-id="element-10" /> Pagina herladen
            </Button>
          </Card>
        </div>
      </MotionConfig>;
  }
  // Full Screen Practice View
  if (currentView === 'practice') {
    return <MotionConfig reducedMotion="user" data-id="element-11">
        <>
          {/* <PracticeErrorBoundary
                          onReset={() => {
                            setActiveModuleId(null)
                            setCurrentView('dashboard')
                            setIsFocusSession(false)
                            setFocusQuestions([])
                            assessment.clearAssessmentState()
                          }}
                        > */}
          <PracticeSession moduleId={activeModuleId} questions={isFocusSession ? focusQuestions : []} onComplete={assessment.assessmentAttempt ? handleAssessmentComplete : handlePracticeComplete} onExit={() => {
          if (assessment.assessmentAttempt) {
            assessment.abandonCurrentAssessment();
          }
          setActiveModuleId(null);
          setCurrentView('dashboard');
          setIsFocusSession(false);
          setFocusQuestions([]);
        }} onAnswer={assessment.assessmentAttempt ? (qId, correct, choice, payload, errorType, delta) => assessment.handleAnswer(qId, correct, choice, payload, errorType, delta) : (qId, correct, choice, payload, errorType, delta) => {
          const question = QUESTIONS.find(q => q.id === qId);
          if (question) {
            recordAnswer(question.module, qId, correct, choice, payload, errorType, delta);
          }
        }} cheatSheetViewed={activeModuleId ? state.progress[activeModuleId]?.cheatSheetViewed ?? false : true} onCheatSheetView={() => activeModuleId && markCheatSheetViewed(activeModuleId)} isCrossModule={isFocusSession} mode={assessment.assessmentAttempt ? 'assessment' : 'practice'} data-id="element-12" />
          {/* </PracticeErrorBoundary> */}
          {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-13" />}
        </>
      </MotionConfig>;
  }
  // ── ADMIN FULL-SCREEN VIEW ─────────────────────────────────────────
  // Render AdminShell outside AppShellRedesigned to avoid duplicate sidebars
  if (activeAccount?.role === 'admin') {
    const accountsList = Object.values(accounts.accountsById);
    return <MotionConfig reducedMotion="user" data-id="element-14">
        <>
          <AdminOverviewPage accounts={accounts} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} selectedGroup={selectedGroup} onNavigate={view => setCurrentNavView(view as NavView)} onNavigateToLearner={handleNavigateToLearner} allAccounts={accountsList} activeAccountId={accounts.activeAccountId} onSwitchAccount={handleSwitchAccount} onLogout={() => setShowWelcome(true)} questions={QUESTIONS} state={state} onReturnHome={() => {
          // Switch to the first non-admin account to return to normal app
          const firstLearner = accountsList.find(a => a.role === 'learner');
          if (firstLearner) {
            handleSwitchAccount(firstLearner.id);
          }
        }} data-id="element-15" />
          {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-16" />}
        </>
      </MotionConfig>;
  }
  // Main App with Shell (for non-admin users)
  return <MotionConfig reducedMotion="user" data-id="element-17">
      <>
        {/* Viewing Learner Banner (Cross-Portal Connection) */}
        {viewingLearnerId && activeAccount?.role !== 'learner' && currentNavView !== 'parent' && currentNavView !== 'teacher' && currentNavView !== 'admin' && <ViewingLearnerBanner learnerName={currentLearnerDisplayName} portalName={activeAccount?.role === 'parent' ? 'Ouderportaal' : activeAccount?.role === 'teacher' ? 'Docentportaal' : 'Beheer'} onReturn={handleReturnToPortal} data-id="element-18" />}

        <AppShellRedesigned stats={state.stats} displayName={currentLearnerDisplayName} currentView={currentNavView} onNavigate={view => setCurrentNavView(view as NavView)} selectedGroup={selectedGroup} onSelectGroup={setSelectedGroup} accounts={accounts} onSwitchAccount={handleSwitchAccount} onLogout={() => setShowWelcome(true)} data-id="element-19">
          {/* Learner-selection gate */}
          {requiresViewingLearnerSelection && (currentNavView === 'dashboard' || currentNavView === 'modules' || currentNavView === 'progress' || currentNavView === 'assessment' || currentNavView === 'learningLab') && <div className="max-w-2xl mx-auto" data-id="element-20">
                <Card className="p-10 text-center border-2 border-indigo-100 bg-indigo-50/40" data-id="element-21">
                  <AlertCircle className="w-10 h-10 text-indigo-600 mx-auto mb-4" data-id="element-22" />
                  <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-23">
                    Selecteer eerst een leerling
                  </h2>
                  <p className="text-slate-600 font-medium" data-id="element-24">
                    Kies in het ouder- of docentportaal een leerling om
                    voortgang, dashboard en instellingen te bekijken.
                  </p>
                </Card>
              </div>}

          {!requiresViewingLearnerSelection && currentNavView === 'dashboard' && <HomePage modules={MODULES} state={state} selectedGroup={selectedGroup} displayName={currentLearnerDisplayName} onStartPractice={handleStartPractice} onNavigateToModules={() => setCurrentNavView('modules')} onModuleSelect={module => setSelectedModule(module)} data-id="element-25" />}

          {!requiresViewingLearnerSelection && currentNavView === 'modules' && <SubjectsPageRedesigned modules={MODULES} progress={state.progress} selectedGroup={selectedGroup} onModuleSelect={module => setSelectedModule(module)} onStartPractice={handleStartPractice} data-id="element-26" />}

          {!requiresViewingLearnerSelection && currentNavView === 'learningLab' && <LearningLabPage onStartAssessment={() => setCurrentNavView('assessment')} onStartMode={handleStartMode} assessmentHistory={getAssessmentHistory(selectedGroup)} data-id="element-27" />}

          {!requiresViewingLearnerSelection && currentNavView === 'assessment' && <AssessmentStartPage selectedGroup={selectedGroup} onStart={handleStartAssessment} onCancel={() => setCurrentNavView('dashboard')} data-id="element-28" />}

          {!requiresViewingLearnerSelection && currentNavView === 'assessmentReport' && assessment.assessmentReport && <AssessmentReportPage report={assessment.assessmentReport} onExit={() => {
          assessment.setAssessmentReport(null);
          setCurrentNavView('dashboard');
        }} onRetake={() => {
          assessment.setAssessmentReport(null);
          setCurrentNavView('assessment');
        }} onPracticeWeakAreas={() => {
          const weakModule = assessment.assessmentReport?.actionPlan?.beginMet?.moduleId;
          assessment.setAssessmentReport(null);
          if (weakModule) {
            handleStartPractice(weakModule as ModuleId);
          } else {
            setCurrentNavView('modules');
          }
        }} data-id="element-29" />}

          {!requiresViewingLearnerSelection && currentNavView === 'progress' && <ProgressPageRedesigned state={state} selectedGroup={selectedGroup} data-id="element-30" />}

          {currentNavView === 'parent' && <ParentPortalPage onNavigateToLearner={handleNavigateToLearner} data-id="element-31" />}
          {currentNavView === 'teacher' && <TeacherPortalPage onNavigateToLearner={handleNavigateToLearner} data-id="element-32" />}

          {currentNavView === 'settings' && <SettingsPage state={state} selectedGroup={selectedGroup} startingPeriod={startingPeriod} manualPeriod={manualPeriod} onSetStartingPeriod={setStartingPeriod} onUpdateName={updateProfileDisplayName} accounts={accounts} onSwitchAccount={handleSwitchAccount} onLogout={() => setShowWelcome(true)} data-id="element-33" />}

          {currentNavView === 'governance' && <GovernancePage data-id="element-34" />}

          {currentNavView === 'developer' && <DeveloperPage questions={QUESTIONS} state={state} selectedGroup={selectedGroup} onNavigate={view => setCurrentNavView(view as NavView)} data-id="element-35" />}
        </AppShellRedesigned>

        {/* Extracted Modals */}
        {assessment.recoverableSession && <AssessmentRecoveryModal session={assessment.recoverableSession} onResume={handleResumeAssessment} onAbandon={assessment.abandonRecovery} data-id="element-36" />}

        {validationError && <ValidationErrorModal error={validationError} selectedGroup={selectedGroup} onClose={() => setValidationError(null)} onSwitchToGroup6={() => {
        setSelectedGroup(6);
        setValidationError(null);
      }} onNavigateToModules={() => {
        setValidationError(null);
        setCurrentNavView('modules');
      }} data-id="element-37" />}

        {assessment.assessmentError && <AssessmentErrorModal message={assessment.assessmentError} onClose={() => assessment.setAssessmentError(null)} data-id="element-38" />}

        {/* Panels */}
        <ModulePanelRedesigned isOpen={!!selectedModule} onClose={() => setSelectedModule(null)} module={selectedModule} progress={selectedModule ? state.progress[selectedModule.id] : null} onStartPractice={moduleId => handleStartPractice(moduleId as ModuleId)} onViewExplanation={moduleId => {
        setCheatSheetModuleId(moduleId as ModuleId);
        setSelectedModule(null);
      }} selectedGroup={selectedGroup} data-id="element-39" />

        <CheatSheetDrawer isOpen={!!cheatSheetModuleId} onClose={() => setCheatSheetModuleId(null)} moduleId={cheatSheetModuleId!} data-id="element-40" />

        {state.pendingLevelUp && <LevelUpModal isOpen={true} level={state.pendingLevelUp} xp={state.stats.xp} onClose={clearPendingLevelUp} data-id="element-41" />}
      </>
    </MotionConfig>;
}